// src/data/diagnostic.ts
//
// INTENTIONAL SHAPE NOTE — choices: string[]
//
// DiagnosticItem uses plain string[] for choices, which differs from the
// canonical DB storage shape for Question.choices ({label, text}[]). This is
// deliberate: the diagnostic is a static file that never touches the Question
// table, with its own type, render path, and test pin
// (src/data/__tests__/diagnostic.test.ts). The render bridge handles both
// shapes. Do NOT "fix" choices to match the DB shape.
//
// DIFFICULTY: this set is intentionally MEDIUM-to-HARD (no gimmes) — the free
// diagnostic is a first impression and should feel like the real SAT.
//
// FIXED SET: the same 20 questions for every user, so the estimated score is
// comparable across people.
//
// NO REPEATS: every item is self-contained — its own passage / table / chart.
// (Matches the digital-SAT one-stimulus-per-question format.)
//
// VISUALS: exactly 10 of the 20 items embed a TABLE or CHART in the stem (a
// markdown pipe-table, or a ```chart fenced block — see chartBlock() below).
// These render through <StemRenderer> on /try/test and /try/results.
//
// Constraints pinned by diagnostic.test.ts (keep in sync when editing):
//   • 20 items; 5 each Reading / Writing / Math-NC / Math-Calc
//   • correct-answer letters balanced EXACTLY 5 per A/B/C/D (keep each item's
//     answer position when editing)
//   • every math item's key matches its real answer (mirror in correctChoiceFor)
//   • W&L underline + NO-CHANGE convention (Choice A = "NO CHANGE"; the
//     underlined text is NEVER repeated verbatim as a choice)

export type DiagnosticSection =
  | 'reading'
  | 'writing'
  | 'math_nc' // math - no calculator
  | 'math_calc' // math - calculator

export type DiagnosticItem = {
  id: string // stable id inside the diagnostic (e.g. "R1", "W6", "MNC11", etc.)
  section: DiagnosticSection
  topic: string // e.g. "Main Idea", "Subject-Verb", "Linear Equation"
  stem: string // question text; may embed a markdown table or a ```chart block
  /** 4 answer choices as plain strings (A=index 0, B=1, C=2, D=3). */
  choices: string[]
  answerIndex: number // 0-based index of the correct answer in 'choices'
  passage?: string // optional (Reading) — rendered as plain text above the stem
  passage2?: string // optional (Paired passage)
  /** 1-2 sentence rationale shown on the per-question review on /try/results. */
  explanation?: string
}

export const diagnosticMeta = {
  code: 'diagnostic_free', // key used for lookups
  title: 'Free Diagnostic (20 Qs)',
  durationMin: 25, // whole-test duration (single section for simplicity)
}

/**
 * Wrap a chart spec in the ```chart fenced block <StemRenderer> looks for.
 * Keeping the spec as a real object (not a hand-written string) means it's
 * validated by diagnostic-charts.test.ts via validateChartSpec().
 */
const chartBlock = (spec: Record<string, unknown>): string =>
  '```chart\n' + JSON.stringify(spec) + '\n```'

export const diagnosticItems: DiagnosticItem[] = [
  // 📖 Reading (5) — each its own passage/table. R3–R5 are quantitative
  // command-of-evidence items built on a data TABLE.
  {
    id: 'R1',
    section: 'reading',
    topic: 'Main Idea',
    passage: `Marine biologists once treated coral bleaching—the loss of the algae that give coral its color and much of its food—as a near-certain death sentence. Newer fieldwork tells a more complicated story: some bleached colonies, if the surrounding water cools quickly enough, reacquire their algae and recover, and a few even return hardier than before. That window for recovery, however, is narrow, and it shrinks as marine heat waves grow longer and more frequent.`,
    stem: 'Which choice best states the main idea of the passage?',
    choices: [
      'Coral bleaching is not always fatal, but the conditions for recovery are limited.',
      'Coral reefs are no longer meaningfully threatened by warming oceans.',
      'Bleached coral colonies reliably return hardier than they were before.',
      'Scientists cannot explain why some bleached coral recovers.',
    ],
    answerIndex: 0,
    explanation:
      'The passage says recovery is possible but only under narrow, shrinking conditions — the qualified claim in A. B contradicts the warning about heat waves; C overstates "a few"; D is wrong because the passage gives the explanation (quick cooling).',
  },
  {
    id: 'R2',
    section: 'reading',
    topic: 'Purpose / Inference',
    passage: `For decades, traffic engineers judged a street by a single number: how many cars it could move per hour. A competing view now gaining ground asks a different question—how many people the street serves, on foot, on bicycles, in buses, and in shops. By that measure, a narrow street lined with destinations can outperform a wide arterial that merely speeds vehicles through.`,
    stem: 'The “competing view” described in the passage values a street primarily for its ability to:',
    choices: [
      'serve many people in many ways, not merely move vehicles quickly',
      'carry the largest possible number of cars per hour',
      'be as wide as the surrounding land will allow',
      'remove buses and bicycles from busy corridors',
    ],
    answerIndex: 0,
    explanation:
      'The competing view counts people served in many modes (A). Moving the most cars (B) and maximal width (C) describe the OLD measure the passage contrasts; D is the opposite of "serves people … on bicycles, in buses."',
  },
  {
    id: 'R3',
    section: 'reading',
    topic: 'Command of Evidence (Data)',
    stem: 'A researcher claimed that, among four city parks, visitors stayed longer at the parks with more shaded seating. The table summarizes the survey.\n\n| Park | Shaded seats | Avg. visit (min) |\n| --- | --- | --- |\n| Alder | 12 | 34 |\n| Birch | 30 | 52 |\n| Cedar | 8 | 28 |\n| Dawn | 22 | 41 |\n\nWhich statement is best supported by the data in the table?',
    // Correct at D (idx 3).
    choices: [
      'Birch drew more total visitors than any other park.',
      'Cedar, with the fewest shaded seats, had the longest average visit.',
      'Average visit length was about the same at all four parks.',
      'Each park with more shaded seats also had a longer average visit.',
    ],
    answerIndex: 3,
    explanation:
      'Ordering by shaded seats (Cedar 8 < Alder 12 < Dawn 22 < Birch 30) gives the same order in average visit (28 < 34 < 41 < 52), so D holds. A is about visitor counts (not in the table); B and C contradict the numbers.',
  },
  {
    id: 'R4',
    section: 'reading',
    topic: 'Command of Evidence (Data)',
    stem: 'A survey asked 200 students how they usually get to school. The results are shown.\n\n| Mode | Students |\n| --- | --- |\n| Walk | 64 |\n| Bike | 40 |\n| Bus | 72 |\n| Car | 24 |\n\nWhich claim about the surveyed students is best supported by the table?',
    // Correct at B (idx 1).
    choices: [
      'More students bike than ride the bus.',
      'More students walk or bike than ride the bus or come by car.',
      'The car is the most common way students get to school.',
      'Exactly half of the students walk.',
    ],
    answerIndex: 1,
    explanation:
      'Walk + bike = 64 + 40 = 104, while bus + car = 72 + 24 = 96, so B is supported. A is false (40 < 72); C is false (car is least, 24); D is false (64/200 = 32%).',
  },
  {
    id: 'R5',
    section: 'reading',
    topic: 'Command of Evidence (Data)',
    stem: 'Researchers measured the germination rate of one crop’s seeds after a year of storage at four temperatures.\n\n| Storage temp (°C) | Germination rate |\n| --- | --- |\n| 5 | 88% |\n| 15 | 79% |\n| 25 | 61% |\n| 35 | 34% |\n\nWhich conclusion is best supported by the data?',
    // Correct at A (idx 0).
    choices: [
      'Germination rate tended to fall as storage temperature rose.',
      'Seeds stored at 25 °C germinated at the highest rate.',
      'Storage temperature had no clear effect on germination.',
      'Germination was highest at the warmest storage temperature.',
    ],
    answerIndex: 0,
    explanation:
      'As temperature rises 5 → 35 °C, germination falls steadily 88% → 34%, so A is supported. B and D contradict the table (the highest rate is at the COLDEST temperature); C ignores the clear downward trend.',
  },

  // ✍️ Writing & Language (5) — text only (W&L doesn't use figures). Underline +
  // NO-CHANGE convention except Sentence Structure (full sentences) and
  // Transitions (blank-fill); the test pins those two exceptions by topic name.
  {
    id: 'W6',
    section: 'writing',
    topic: 'Subject–Verb Agreement',
    stem: "Along the canyon's rim, a series of weathered markers __indicate__ the trail's safest route.",
    choices: ['NO CHANGE', 'have indicated', 'are indicating', 'indicates'],
    answerIndex: 3,
    explanation:
      'The subject is "a series" (singular), not "markers" (object of the preposition "of"), so the verb must be singular: "indicates." The inverted opening phrase doesn\'t change the subject.',
  },
  {
    // REPLACED 2026-09-11. The previous item keyed "he or she had" over singular
    // "they" for "Neither of the two finalists" — a contested usage that APA,
    // Chicago and Merriam-Webster all accept, and which the explanation claimed
    // "the SAT tests" without warrant. It was also the bank's ONLY Pronoun
    // Agreement item, so missing it put a student at 0% on the topic and made
    // it their headline weakness with a drill attached — a confident
    // recommendation resting on a disputed call.
    //
    // The skill is worth testing, so it is kept and made unambiguous instead:
    // the antecedent is now inanimate, which removes singular "they" from the
    // question entirely while preserving the actual trap — an intervening
    // prepositional phrase between a singular indefinite pronoun and its
    // referent.
    id: 'W7',
    section: 'writing',
    topic: 'Pronoun Agreement',
    stem: 'Each of the weather stations along the ridge transmits __their__ readings to a single relay tower.',
    choices: ['NO CHANGE', "they're", "it's", 'its'],
    answerIndex: 3,
    explanation:
      '"Each" is singular, so the pronoun standing in for it must be singular too: "its." The phrase "of the weather stations along the ridge" sits between them and invites a plural, but a prepositional phrase never changes what the pronoun agrees with. "It\'s" is short for "it is," which would read "transmits it is readings."',
  },
  {
    id: 'W8',
    section: 'writing',
    topic: 'Sentence Structure',
    stem: 'The committee reviewed the proposal for months, in the end they rejected it.',
    choices: [
      'The committee reviewed the proposal for months; in the end, they rejected it.',
      'The committee reviewed the proposal for months, in the end, they rejected it.',
      'The committee reviewed the proposal for months in the end they rejected it.',
      'The committee reviewed the proposal for months, in the end they rejected it, however.',
    ],
    answerIndex: 0,
    explanation:
      'Two independent clauses need stronger separation than a comma; a semicolon does that (A). B is still a comma splice, C is a run-on with no punctuation, and D adds a dangling "however" without fixing the splice.',
  },
  {
    id: 'W9',
    section: 'writing',
    topic: 'Concision',
    stem: 'The two labs __collaborated together as a team__ to design the experiment.',
    choices: [
      'NO CHANGE',
      'collaborated together',
      'worked collaboratively as a team',
      'collaborated',
    ],
    answerIndex: 3,
    explanation:
      '"Collaborated" already means "worked together as a team," so "together as a team" is redundant. The concise choice that keeps the meaning is "collaborated." B and C still repeat the idea.',
  },
  {
    id: 'W10',
    section: 'writing',
    topic: 'Transitions',
    stem: 'The new toll was meant to reduce congestion. ___, traffic in the city center has scarcely changed since it took effect.',
    choices: ['For example', 'As a result', 'Likewise', 'Nevertheless'],
    answerIndex: 3,
    explanation:
      'The intended effect (less congestion) and the actual outcome (almost no change) clash, so the transition must signal contrast: "Nevertheless." "For example," "As a result," and "Likewise" signal illustration, cause-effect, and similarity — none fit.',
  },

  // ➗ Math – No Calculator (5) — MNC13 and MNC15 are TABLE-based.
  {
    id: 'MNC11',
    section: 'math_nc',
    topic: 'Linear Equation',
    stem: 'If 2(x − 3) = 3x + 4, what is the value of x?',
    choices: ['x = -2', 'x = -10', 'x = 2', 'x = 10'],
    answerIndex: 1,
    explanation:
      'Distribute: 2x − 6 = 3x + 4. Subtract 2x from both sides: −6 = x + 4, so x = −10.',
  },
  {
    id: 'MNC12',
    section: 'math_nc',
    topic: 'System of Equations',
    stem: 'If 2x + 3y = 13 and x − y = −1, what is the value of x?',
    choices: ['x = 1', 'x = 2', 'x = 3', 'x = 4'],
    answerIndex: 1,
    explanation:
      'From x − y = −1, x = y − 1. Substitute into 2x + 3y = 13: 2(y − 1) + 3y = 13 → 5y − 2 = 13 → y = 3, so x = y − 1 = 2.',
  },
  {
    id: 'MNC13',
    section: 'math_nc',
    topic: 'Linear Function (table)',
    stem: 'The table shows several values of a linear function f.\n\n| x | f(x) |\n| --- | --- |\n| 1 | 5 |\n| 3 | 11 |\n| 5 | 17 |\n| 7 | 23 |\n\nWhat is the value of f(0)?',
    choices: ['1', '2', '3', '5'],
    answerIndex: 1,
    explanation:
      'f(x) rises by 6 for every increase of 2 in x, so the slope is 3 and f(x) = 3x + 2. Then f(0) = 2.',
  },
  {
    id: 'MNC14',
    section: 'math_nc',
    topic: 'Rational Expression',
    stem: 'For x ≠ −3, simplify (2x² + 5x − 3) / (x + 3).',
    choices: ['2x - 1', '2x + 1', 'x - 3', '2x - 3'],
    answerIndex: 0,
    explanation:
      'Factor the numerator: 2x² + 5x − 3 = (2x − 1)(x + 3). The (x + 3) cancels with the denominator, leaving 2x − 1.',
  },
  {
    id: 'MNC15',
    section: 'math_nc',
    topic: 'Rate (table)',
    stem: 'The table shows how far a cyclist had traveled at several times.\n\n| Time (min) | Distance (km) |\n| --- | --- |\n| 0 | 0 |\n| 10 | 4 |\n| 20 | 8 |\n| 30 | 12 |\n\nAt this constant rate, how far will the cyclist have traveled after 45 minutes?',
    choices: ['14 km', '16 km', '18 km', '20 km'],
    answerIndex: 2,
    explanation:
      'The cyclist covers 4 km every 10 minutes, i.e. 0.4 km per minute. After 45 minutes: 0.4 × 45 = 18 km.',
  },

  // 📊 Math – Calculator (5) — all five are CHART/TABLE based.
  {
    id: 'MC16',
    section: 'math_calc',
    topic: 'Statistics (bar chart)',
    stem:
      'The bar chart shows the number of books five students read last month.\n\n' +
      chartBlock({
        type: 'bar',
        title: 'Books read last month',
        xAxis: { label: 'Student', key: 'student' },
        yAxis: { label: 'Books', key: 'books' },
        data: [
          { student: 'A', books: 3 },
          { student: 'B', books: 5 },
          { student: 'C', books: 4 },
          { student: 'D', books: 8 },
          { student: 'E', books: 5 },
        ],
        series: [{ key: 'books', name: 'Books read', color: '#22d3ee' }],
      }) +
      '\n\nWhat is the mean number of books read?',
    choices: ['4', '5', '6', '8'],
    answerIndex: 1,
    explanation: 'Mean = (3 + 5 + 4 + 8 + 5) ÷ 5 = 25 ÷ 5 = 5.',
  },
  {
    id: 'MC17',
    section: 'math_calc',
    topic: 'Probability (two-way table)',
    stem: 'The two-way table shows survey responses by grade.\n\n| | Likes | Dislikes | Total |\n| --- | --- | --- | --- |\n| Grade 9 | 30 | 20 | 50 |\n| Grade 10 | 45 | 15 | 60 |\n| Total | 75 | 35 | 110 |\n\nIf one of the 110 students is chosen at random, what is the probability that the student is in Grade 10 and dislikes?',
    choices: ['7/22', '1/4', '3/22', '5/11'],
    answerIndex: 2,
    explanation:
      '15 of the 110 students are both in Grade 10 and dislike, so the probability is 15/110 = 3/22. (7/22 is the overall dislike rate; 1/4 is the dislike rate within Grade 10.)',
  },
  {
    id: 'MC18',
    section: 'math_calc',
    topic: 'Rate of Change (line graph)',
    stem:
      'The line graph shows the balance in a savings account over four months.\n\n' +
      chartBlock({
        type: 'line',
        title: 'Account balance',
        xAxis: { label: 'Month', key: 'month' },
        yAxis: { label: 'Balance ($)', key: 'balance' },
        data: [
          { month: 0, balance: 200 },
          { month: 1, balance: 260 },
          { month: 2, balance: 320 },
          { month: 3, balance: 380 },
        ],
        series: [{ key: 'balance', name: 'Balance', color: '#22c55e' }],
      }) +
      '\n\nThe balance grows at a constant rate. By how much does it increase each month?',
    choices: ['$40', '$50', '$60', '$80'],
    answerIndex: 2,
    explanation:
      'The balance rises from $200 to $380 over 3 months — a constant $60 per month (for example, 260 − 200 = 60).',
  },
  {
    id: 'MC19',
    section: 'math_calc',
    topic: 'Scatterplots (association)',
    stem:
      'The scatterplot shows six students’ study time and test scores.\n\n' +
      chartBlock({
        type: 'scatter',
        title: 'Study time vs. score',
        xAxis: { label: 'Hours studied', key: 'hours' },
        yAxis: { label: 'Score', key: 'score' },
        data: [
          { hours: 1, score: 62 },
          { hours: 2, score: 68 },
          { hours: 3, score: 74 },
          { hours: 4, score: 79 },
          { hours: 5, score: 86 },
          { hours: 6, score: 90 },
        ],
        series: [{ key: 'score', name: 'Score', color: '#a78bfa' }],
      }) +
      '\n\nWhich best describes the association between hours studied and score?',
    choices: [
      'No association',
      'Negative linear association',
      'Positive linear association',
      'Exponential decay',
    ],
    answerIndex: 2,
    explanation:
      'As hours studied increase, scores rise at a roughly steady rate, so the association is positive and approximately linear.',
  },
  {
    id: 'MC20',
    section: 'math_calc',
    topic: 'Data Analysis (bar chart)',
    stem:
      'Eighty students each voted for one favorite fruit; the results are shown.\n\n' +
      chartBlock({
        type: 'bar',
        title: 'Favorite fruit',
        xAxis: { label: 'Fruit', key: 'fruit' },
        yAxis: { label: 'Votes', key: 'votes' },
        data: [
          { fruit: 'Apple', votes: 24 },
          { fruit: 'Banana', votes: 16 },
          { fruit: 'Cherry', votes: 32 },
          { fruit: 'Grape', votes: 8 },
        ],
        series: [{ key: 'votes', name: 'Votes', color: '#f472b6' }],
      }) +
      '\n\nWhat percent of the students chose cherry?',
    choices: ['20%', '30%', '40%', '50%'],
    answerIndex: 2,
    explanation: '32 of the 80 students chose cherry: 32 ÷ 80 = 0.40 = 40%.',
  },
]
